import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const MENTOR_TYPES = [
  { v: "DOCTORAL_GRADUATE", l: "Doctoral Graduate" },
  { v: "FACULTY", l: "Faculty" },
  { v: "DISSERTATION_COACH", l: "Dissertation Coach" },
  { v: "RESEARCH_METHODOLOGY_MENTOR", l: "Research / Methodology Mentor" },
];
const EXPERTISE_OPTIONS = [
  "narrative_research", "phenomenology", "case_study", "grounded_theory", "ethnography",
  "experimental", "quasi_experimental", "correlational", "survey_research", "statistical_analysis",
  "mixed_methods",
  "irb", "proposal_development", "literature_review", "academic_writing",
  "data_analysis", "defense_preparation", "publishing", "career_transition",
];
const STAGES = [
  "coursework", "comps", "topic_development", "proposal", "proposal_defense",
  "irb", "research", "analysis", "dissertation", "final_review",
  "defense", "revisions", "graduation", "publishing",
];

export default function MentorProfileEdit() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isMentor, setIsMentor] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    mentor_type: "FACULTY",
    headline: "",
    bio: "",
    mentoring_approach: "",
    discipline: "",
    degree_type: "",
    methodology: "",
    methodology_subtype: "",
    research_areas: [],
    supported_stages: [],
    expertise: [],
    languages: [],
    country: "",
    timezone: "",
    university: "",
    university_public: false,
    years_experience: "",
    availability: "AVAILABLE",
    discoverable: true,
  });
  const [verificationStatus, setVerificationStatus] = useState("UNVERIFIED");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/mentors/me");
      if (data.is_mentor) {
        setIsMentor(true);
        setForm({ ...form, ...data.profile,
                  years_experience: data.profile.years_experience ?? "" });
        setVerificationStatus(data.profile.verification_status || "UNVERIFIED");
      }
    } catch (e) { /* not a mentor yet — that's fine */ }
    finally { setLoading(false); }
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const toggleItem = (key, val) => {
    setForm(f => ({ ...f, [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val] }));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form,
        years_experience: form.years_experience === "" ? null : Number(form.years_experience),
      };
      if (isMentor) {
        await api.put("/mentors/me", payload);
        toast.success("Mentor profile updated");
      } else {
        await api.post("/mentors/me", payload);
        toast.success("You're now opted in as a mentor");
        setIsMentor(true);
      }
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="mentor-profile-edit">
      <header>
        <h1 className="font-serif text-3xl font-black tracking-tight">
          {isMentor ? "Manage your mentor profile" : "Become a Mentor"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Only fields you publish here are visible in the Mentor Network — your private doctoral profile stays private.
        </p>
      </header>

      <Card className="p-5 space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Mentor type</Label>
            <select data-testid="edit-mentor-type" className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              value={form.mentor_type} onChange={e => setForm({ ...form, mentor_type: e.target.value })}>
              {MENTOR_TYPES.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <div>
            <Label>Availability</Label>
            <select data-testid="edit-availability" className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              value={form.availability} onChange={e => setForm({ ...form, availability: e.target.value })}>
              <option value="AVAILABLE">Available</option>
              <option value="LIMITED">Limited</option>
              <option value="NOT_ACCEPTING">Not currently accepting</option>
            </select>
          </div>
        </div>

        <div>
          <Label>Professional headline</Label>
          <Input data-testid="edit-headline" maxLength={200} value={form.headline}
            onChange={e => setForm({ ...form, headline: e.target.value })}
            placeholder="e.g. Cybersecurity PhD advisor — 20 years supporting doctoral candidates" />
        </div>

        <div>
          <Label>Biography</Label>
          <Textarea data-testid="edit-bio" rows={4} maxLength={3000} value={form.bio}
            onChange={e => setForm({ ...form, bio: e.target.value })} />
        </div>

        <div>
          <Label>Mentoring approach</Label>
          <Textarea data-testid="edit-approach" rows={3} maxLength={1000} value={form.mentoring_approach}
            onChange={e => setForm({ ...form, mentoring_approach: e.target.value })} />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Discipline</Label>
            <Input data-testid="edit-discipline" value={form.discipline}
              onChange={e => setForm({ ...form, discipline: e.target.value })} />
          </div>
          <div>
            <Label>Highest doctoral degree</Label>
            <select data-testid="edit-degree" className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              value={form.degree_type} onChange={e => setForm({ ...form, degree_type: e.target.value })}>
              <option value="">—</option>
              {["PhD","EdD","DBA","DNP","PsyD","DrPH","Masters"].map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <Label>Methodology</Label>
            <select data-testid="edit-methodology" className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              value={form.methodology} onChange={e => setForm({ ...form, methodology: e.target.value })}>
              <option value="">—</option>
              <option value="Qualitative">Qualitative</option>
              <option value="Quantitative">Quantitative</option>
              <option value="Mixed Methods">Mixed Methods</option>
            </select>
          </div>
          <div>
            <Label>Methodology subtype</Label>
            <Input data-testid="edit-subtype" value={form.methodology_subtype}
              onChange={e => setForm({ ...form, methodology_subtype: e.target.value })}
              placeholder="e.g. Narrative Research" />
          </div>
        </div>

        <div>
          <Label>Areas I can help with</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {EXPERTISE_OPTIONS.map(e => (
              <button key={e} type="button" data-testid={`expertise-${e}`}
                onClick={() => toggleItem("expertise", e)}
                className={`text-xs px-2 py-1 rounded-md border ${form.expertise?.includes(e) ? "bg-primary text-primary-foreground border-primary" : "border-input bg-background hover:bg-muted"}`}>
                {e.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label>Stages I support</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {STAGES.map(s => (
              <button key={s} type="button" data-testid={`stage-${s}`}
                onClick={() => toggleItem("supported_stages", s)}
                className={`text-xs px-2 py-1 rounded-md border ${form.supported_stages?.includes(s) ? "bg-primary text-primary-foreground border-primary" : "border-input bg-background hover:bg-muted"}`}>
                {s.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Research areas (comma-separated)</Label>
            <Input data-testid="edit-research-areas" value={(form.research_areas || []).join(", ")}
              onChange={e => setForm({ ...form, research_areas: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} />
          </div>
          <div>
            <Label>Languages (comma-separated)</Label>
            <Input data-testid="edit-languages" value={(form.languages || []).join(", ")}
              onChange={e => setForm({ ...form, languages: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} />
          </div>
          <div>
            <Label>Country</Label>
            <Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} />
          </div>
          <div>
            <Label>Timezone</Label>
            <Input value={form.timezone} onChange={e => setForm({ ...form, timezone: e.target.value })} />
          </div>
          <div>
            <Label>University / Institution</Label>
            <Input value={form.university} onChange={e => setForm({ ...form, university: e.target.value })} />
            <label className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={form.university_public}
                onChange={e => setForm({ ...form, university_public: e.target.checked })} />
              Show institution on my public mentor profile
            </label>
          </div>
          <div>
            <Label>Years of experience</Label>
            <Input type="number" min="0" max="80" value={form.years_experience}
              onChange={e => setForm({ ...form, years_experience: e.target.value })} />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2 border-t">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="edit-discoverable" checked={!!form.discoverable}
              onChange={e => setForm({ ...form, discoverable: e.target.checked })} />
            Discoverable in the Mentor Network
          </label>
          <span className="ml-auto text-xs text-muted-foreground">
            Verification: <span className="font-medium">{verificationStatus}</span>
          </span>
        </div>
      </Card>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={() => nav("/mentors")}>Cancel</Button>
        <Button data-testid="save-mentor-profile" disabled={busy} onClick={submit}>
          {isMentor ? "Save changes" : "Publish mentor profile"}
        </Button>
      </div>
    </div>
  );
}
